import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled, { createGlobalStyle } from "styled-components";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import {
  cdtStudioActionRequestAtom,
  cdtStudioExceptionCardsAtom,
  cdtStudioPinsAtom,
  cdtStudioSelectedFlagAtom,
  cdtStudioSelectionSourceAtom,
} from "../store/atoms";
import { useTranslation } from "src/i18n";
import { STUDIO_THEME } from "../constants/studioTheme";

// A pinned item must paint above its neighbours, or the badge disappears
// under the next product image (each item is its own stacking context).
const PinStacking = createGlobalStyle`
  .planogram-item:has([data-cdt-studio-pin]) {
    z-index: 20;
  }
`;

const PinButton = styled.button<{ $selected: boolean }>`
  position: absolute;
  /* Fully inside the item box: anything poking out gets clipped by the
     shelf's overflow or painted over by the next product image. */
  top: 2px;
  right: 2px;
  z-index: 12;
  /* The planogram layers disable pointer-events wholesale (and a broad rule
     re-disables buttons), so the pin has to force itself clickable. */
  pointer-events: auto !important;
  min-width: 1.1rem;
  height: 1.1rem;
  padding: 0 0.2rem;
  border: 1px solid #ffffff;
  border-radius: 999px;
  background: #d64545;
  color: #ffffff;
  font-size: 0.65rem;
  font-weight: 800;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.35);
  transition:
    transform 120ms ease,
    box-shadow 120ms ease,
    filter 120ms ease;

  &:hover {
    transform: scale(1.08);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.42);
    filter: brightness(1.08);
  }

  &:focus-visible {
    outline: 2px solid ${STUDIO_THEME.primary};
    outline-offset: 2px;
  }

  ${({ $selected }) =>
    $selected &&
    `
    outline: 2px solid #d64545;
    outline-offset: 2px;
  `}
`;

const Card = styled.div`
  position: fixed;
  z-index: 60;
  width: 300px;
  padding: 0.625rem 0.75rem;
  border: 1px solid ${STUDIO_THEME.border};
  border-radius: 0.5rem;
  background: #ffffff;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  font-size: 0.75rem;
  color: ${STUDIO_THEME.text};
  pointer-events: auto;
`;

const CardTag = styled.span`
  align-self: flex-start;
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
  background: ${STUDIO_THEME.severity.blocking.background};
  color: ${STUDIO_THEME.severity.blocking.text};
  border: 1px solid ${STUDIO_THEME.severity.blocking.border};
  font-size: 0.625rem;
  font-weight: 800;
`;

const CardAction = styled.button<{ $primary?: boolean }>`
  padding: 0.25rem 0.625rem;
  border-radius: 0.375rem;
  border: 1px solid ${({ $primary }) => ($primary ? STUDIO_THEME.primary : STUDIO_THEME.border)};
  background: ${({ $primary }) => ($primary ? STUDIO_THEME.primary : "transparent")};
  color: ${({ $primary }) => ($primary ? "#ffffff" : STUDIO_THEME.text)};
  font-size: 0.6875rem;
  font-weight: 700;
  cursor: pointer;
`;

interface Props {
  uniqueId: string;
}

interface PinCardProps {
  flagId: string;
  number: number;
  onClose: () => void;
  position: { left: number; top: number };
}

export const CdtStudioPinCard = ({ flagId, number, onClose, position }: PinCardProps) => {
  const { t } = useTranslation("cdtStudio");
  const cards = useAtomValue(cdtStudioExceptionCardsAtom);
  const dispatchAction = useSetAtom(cdtStudioActionRequestAtom);
  const card = cards[flagId];

  if (!card) return null;
  return createPortal(
    <Card
      style={{ left: position.left, top: position.top }}
      data-testid={`cdt-studio-pin-card-${number}`}
      onClick={(event) => event.stopPropagation()}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
        <CardTag>
          {card.number} · {card.kind}
        </CardTag>
        <span style={{ color: STUDIO_THEME.textSecondary, fontSize: "0.6875rem" }}>
          {card.location}
        </span>
        <span style={{ flexGrow: 1 }} />
        <CardAction type="button" onClick={onClose}>
          ✕
        </CardAction>
      </div>
      <strong>{card.title}</strong>
      {card.detail && <span style={{ color: STUDIO_THEME.textSecondary }}>{card.detail}</span>}
      {card.built && (
        <span style={{ color: STUDIO_THEME.textSecondary }}>
          {t("evidence.built", { order: card.built })}
        </span>
      )}
      {card.want && (
        <span style={{ color: STUDIO_THEME.text }}>{t("evidence.want", { order: card.want })}</span>
      )}
      <span>{card.suggestion}</span>
      <div style={{ display: "flex", gap: "6px", marginTop: "2px" }}>
        {card.canAutoResolve && (
          <CardAction
            type="button"
            $primary
            onClick={() => dispatchAction({ type: "resolve", flagId })}
          >
            {t("resolver.autoResolve")}
          </CardAction>
        )}
        <CardAction type="button" onClick={() => dispatchAction({ type: "accept", flagId })}>
          {t("evidence.accept")}
        </CardAction>
        <CardAction type="button" onClick={() => dispatchAction({ type: "dismiss", flagId })}>
          {t("evidence.dismiss")}
        </CardAction>
      </div>
    </Card>,
    document.body,
  );
};

export const CdtStudioItemPin = ({ uniqueId }: Props) => {
  const pins = useAtomValue(cdtStudioPinsAtom);
  const [selectedFlag, setSelectedFlag] = useAtom(cdtStudioSelectedFlagAtom);
  const setSelectionSource = useSetAtom(cdtStudioSelectionSourceAtom);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [cardPosition, setCardPosition] = useState<{ left: number; top: number } | null>(null);

  const pin = pins[uniqueId];
  const selected = pin ? selectedFlag === pin.flagId : false;

  // Anchor the card beside the pin, clamped to the viewport; follow the
  // planogram as it scrolls under the card. Selection triggers an overlay
  // retreat + scroll + item remounts, so the pin's rect can be degenerate
  // for a few frames — keep retrying on animation frames until it is real.
  useEffect(() => {
    if (!selected) {
      setCardPosition(null);
      return;
    }
    let raf = 0;
    const place = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect || (rect.width === 0 && rect.height === 0)) {
        raf = requestAnimationFrame(place);
        return;
      }
      const left = Math.min(Math.max(rect.right + 8, 8), window.innerWidth - 316);
      const top = Math.min(Math.max(rect.top - 8, 8), window.innerHeight - 240);
      setCardPosition({ left, top });
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [selected]);

  if (!pin) return null;
  return (
    <>
      <PinStacking />
      <PinButton
        ref={buttonRef}
        type="button"
        $selected={selected}
        title={`Exception ${pin.number}`}
        data-cdt-studio-pin
        data-testid={`cdt-studio-pin-${pin.number}`}
        // Native drag/selection listeners on the item run before React bubble handlers.
        // Capture the whole gesture so the item cannot switch the panel to Product details.
        onPointerDownCapture={(event) => event.stopPropagation()}
        onPointerUpCapture={(event) => event.stopPropagation()}
        onMouseDownCapture={(event) => event.stopPropagation()}
        onMouseEnter={() => {
          setSelectionSource("pin");
          setSelectedFlag(pin.flagId);
        }}
        onFocus={() => {
          setSelectionSource("pin");
          setSelectedFlag(pin.flagId);
        }}
        onClickCapture={(event) => {
          event.stopPropagation();
          event.preventDefault();
        }}
      >
        {pin.number}
      </PinButton>
      {selected && cardPosition && (
        <CdtStudioPinCard
          flagId={pin.flagId}
          number={pin.number}
          onClose={() => {
            setSelectedFlag(null);
            setSelectionSource(null);
          }}
          position={cardPosition}
        />
      )}
    </>
  );
};
